// pages/GraphDashboard.jsx
// Graphologist dashboard — rebuilt.
//
// Design brief kept from v1: UXAAH tokens (DM Serif Display / DM Sans,
// --pd #1a2344, --ao #e8622a). What changed is the structure: the handwriting
// specimen is now the primary artifact on every session row instead of a
// 100px afterthought, and rows sit on a ruled-margin baseline borrowed from
// analysis paper. That's the one place the design spends its boldness.
//
// Tabs: Sessions · Calendar · Availability · Insights · Profile
//
// BACKEND ASSUMPTIONS (see notes at bottom of file):
//   GET   /graph/booked-slots
//   GET   /graph/available-slots
//   POST  /graph/add-available-slots
//   DELETE /graph/available-slots/:id
//   PATCH /graph/bookings/:id/notes      { reportNotes, status? }
//   GET   /graphologists/:id
//   PATCH /graphologists/:id             { name, PhoneNumber, specialisation, experience, price, bio }
//   POST  /video-call/initiate           { receiverId, bookingType }

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/authcontext';

const BACKEND_URL =
  import.meta.env?.VITE_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

/* ─── date + money helpers ─────────────────────────────────────────────── */

// Booking rows come back in two shapes depending on the endpoint version:
// { appointment_time } or { date, time }. Safari refuses "YYYY-MM-DD HH:mm",
// so normalise to ISO-ish before constructing.
const sessionDate = (s) => {
  if (!s) return null;
  const raw = s.appointment_time || (s.date ? `${s.date} ${s.time || '00:00'}` : null);
  if (!raw) return null;
  let d = new Date(String(raw).replace(' ', 'T'));
  if (isNaN(d.getTime())) d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

const slotDateOf = (slot) => {
  if (!slot?.start_time) return null;
  const d = new Date(String(slot.start_time).replace(' ', 'T'));
  return isNaN(d.getTime()) ? null : d;
};

const fmtDay = (d) =>
  d ? d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';

const fmtTime = (d) =>
  d ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

const dayKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const isSameDay = (a, b) =>
  !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const rupees = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const KNOWN_STATUSES = ['scheduled', 'completed', 'cancelled'];
const statusClass = (s) => (KNOWN_STATUSES.includes(s) ? s : 'unknown');

const buildMonthCells = (cursor) => {
  const y = cursor.getFullYear();
  const m = cursor.getMonth();
  const pad = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < pad; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

/* ─── stateless bits (module level so they never remount) ──────────────── */

const Specimen = ({ src, onOpen }) =>
  src ? (
    <button type="button" className="gd-spec" onClick={() => onOpen(src)} title="View full specimen">
      <img src={src} alt="Client handwriting specimen" />
      <span className="gd-spec-zoom">View</span>
    </button>
  ) : (
    <div className="gd-spec gd-spec-empty">
      <span>No specimen uploaded</span>
    </div>
  );

const Tile = ({ value, label, sub, tone = 'plain' }) => (
  <div className={`gd-tile tone-${tone}`}>
    <div className="gd-tile-num">{value}</div>
    <div className="gd-tile-lbl">{label}</div>
    {sub && <div className="gd-tile-sub">{sub}</div>}
  </div>
);

/* ─── main ─────────────────────────────────────────────────────────────── */

const GraphDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('sessions');
  const [sessions, setSessions] = useState([]);
  const [slots, setSlots] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // sessions tab controls
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortDir, setSortDir] = useState('asc'); // asc = soonest first

  // calendar
  const [cursor, setCursor] = useState(new Date());
  const [pickedDay, setPickedDay] = useState(null);

  // notes modal
  const [notesTarget, setNotesTarget] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [markDone, setMarkDone] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // availability
  const [slotDate, setSlotDate] = useState('');
  const [slotStart, setSlotStart] = useState('');
  const [slotEnd, setSlotEnd] = useState('');
  const [addingSlot, setAddingSlot] = useState(false);
  const [hideExpired, setHideExpired] = useState(true);

  // profile edit
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);

  const [lightbox, setLightbox] = useState(null);

  /* guard */
  useEffect(() => {
    if (user && user.role !== 'graphologist') navigate('/home', { replace: true });
  }, [user, navigate]);

  /* fetching */
  const fetchSessions = useCallback(async () => {
    try {
      const { data } = await axios.get('/graph/booked-slots');
      setSessions(Array.isArray(data) ? data : Array.isArray(data?.slots) ? data.slots : []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      toast.error('Could not load your sessions');
    }
  }, []);

  const fetchSlots = useCallback(async () => {
    try {
      const { data } = await axios.get('/graph/available-slots');
      setSlots(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching slots:', err);
    }
  }, []);

  const fetchProfile = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await axios.get(`/graphologists/${user.id}`);
      setProfile(data);
    } catch (err) {
      console.error('Error fetching profile:', err);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    (async () => {
      setLoading(true);
      await Promise.allSettled([fetchSessions(), fetchSlots(), fetchProfile()]);
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [user?.id, fetchSessions, fetchSlots, fetchProfile]);

  /* derived */
  const decorated = useMemo(
    () => sessions.map((s) => ({ ...s, _d: sessionDate(s), _status: s.status || 'scheduled' })),
    [sessions]
  );

  const now = new Date();
  const todayCount = decorated.filter((s) => isSameDay(s._d, now)).length;
  const upcomingCount = decorated.filter((s) => s._d && s._d >= now && s._status === 'scheduled').length;
  const completedCount = decorated.filter((s) => s._status === 'completed').length;
  const openSlotCount = slots.filter((s) => s.status !== 'expired' && s.display_status !== 'expired').length;

  const fee = Number(profile?.price) || 0;
  const earned = completedCount * fee;
  const pipeline = upcomingCount * fee;

  const visibleSessions = useMemo(() => {
    const q = query.trim().toLowerCase();
    return decorated
      .filter((s) => {
        if (statusFilter !== 'all' && s._status !== statusFilter) return false;
        if (pickedDay && !isSameDay(s._d, pickedDay)) return false;
        if (!q) return true;
        return [s.client_name, s.client_email, s.client_phone]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => {
        const av = a._d ? a._d.getTime() : Infinity;
        const bv = b._d ? b._d.getTime() : Infinity;
        return sortDir === 'asc' ? av - bv : bv - av;
      });
  }, [decorated, query, statusFilter, sortDir, pickedDay]);

  const sessionsByDay = useMemo(() => {
    const map = {};
    decorated.forEach((s) => {
      if (!s._d) return;
      const k = dayKey(s._d);
      (map[k] = map[k] || []).push(s);
    });
    return map;
  }, [decorated]);

  const slotsByDay = useMemo(() => {
    const map = {};
    slots.forEach((s) => {
      const d = slotDateOf(s);
      if (!d || s.status === 'expired') return;
      const k = dayKey(d);
      map[k] = (map[k] || 0) + 1;
    });
    return map;
  }, [slots]);

  // last 6 months of session volume, for the Insights bars
  const monthlyVolume = useMemo(() => {
    const out = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const count = decorated.filter(
        (s) => s._d && s._d.getFullYear() === d.getFullYear() && s._d.getMonth() === d.getMonth()
      ).length;
      out.push({ label: MONTHS[d.getMonth()], count });
    }
    return out;
  }, [decorated]); // eslint-disable-line react-hooks/exhaustive-deps

  const peakVolume = Math.max(1, ...monthlyVolume.map((m) => m.count));

  /* actions */
  const handleSaveNotes = async () => {
    if (!notesTarget) return;
    setSavingNotes(true);
    try {
      const body = { reportNotes: notesText };
      // v1 always sent status:'completed', which silently closed sessions on
      // every notes edit. Now it's explicit.
      if (markDone) body.status = 'completed';
      await axios.patch(`/graph/bookings/${notesTarget.id}/notes`, body);
      toast.success(markDone ? 'Notes saved, session closed' : 'Notes saved');
      setNotesTarget(null);
      fetchSessions();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAddSlot = async (e) => {
    e.preventDefault();
    if (!slotDate || !slotStart || !slotEnd) return toast.error('Pick a date and a time range');
    if (slotStart >= slotEnd) return toast.error('End time has to be after start time');
    setAddingSlot(true);
    try {
      await axios.post('/graph/add-available-slots', {
        date: slotDate,
        start_time: slotStart,
        end_time: slotEnd,
      });
      toast.success('Slot added');
      setSlotDate(''); setSlotStart(''); setSlotEnd('');
      fetchSlots();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not add slot');
    } finally {
      setAddingSlot(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    const prev = slots;
    setSlots((s) => s.filter((x) => x.id !== slotId)); // optimistic
    try {
      await axios.delete(`/graph/available-slots/${slotId}`);
      toast.success('Slot removed');
    } catch (err) {
      setSlots(prev);
      toast.error('Could not remove slot');
    }
  };

  const handleStartCall = async (session) => {
    try {
      const { data } = await axios.post('/video-call/initiate', {
        receiverId: session.client_id,
        bookingType: 'graphologist',
      });
      if (data?.success) navigate(`/video-chat/${data.roomId}`);
      else toast.error(data?.error || 'Could not start the session');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not start the session — the client may be offline');
    }
  };

  const startEditing = () => {
    setDraft({
      name: profile?.name || '',
      PhoneNumber: profile?.PhoneNumber || '',
      specialisation: profile?.specialisation || '',
      experience: profile?.experience || '',
      price: profile?.price || '',
      bio: profile?.bio || '',
    });
    setEditing(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await axios.patch(`/graphologists/${user.id}`, draft);
      setProfile(data && typeof data === 'object' && data.id ? data : { ...profile, ...draft });
      setEditing(false);
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';

  /* ─── render ─────────────────────────────────────────────────────────── */

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&family=DM+Mono:wght@400;500&display=swap');

        .gd-root {
          --pd: #1a2344;
          --pd-lift: #2d4080;
          --ao: #e8622a;
          --sage: #4a7c6f;
          --paper: #fdfcf9;
          --bg: #f7f8fb;
          --card: #ffffff;
          --border: rgba(26,35,68,0.09);
          --text: #111827;
          --muted: #6b7280;
          --shadow: 0 2px 10px rgba(26,35,68,0.05);
          --shadow-lift: 0 10px 30px rgba(26,35,68,0.11);
          font-family: 'DM Sans', system-ui, sans-serif;
          background: var(--bg);
          min-height: 100vh;
          color: var(--text);
        }
        .gd-root *:focus-visible { outline: 2px solid var(--ao); outline-offset: 2px; border-radius: 4px; }
        @media (prefers-reduced-motion: reduce) {
          .gd-root *, .gd-modal-bg, .gd-lightbox { transition: none !important; animation: none !important; }
        }

        /* header */
        .gd-header {
          background: var(--pd); padding: 0 32px; height: 66px;
          display: flex; align-items: center; justify-content: space-between;
          position: sticky; top: 0; z-index: 100;
        }
        .gd-brand { font-family: 'DM Serif Display', serif; color: #fff; font-size: 20px; letter-spacing: 0.06em; }
        .gd-brand span { color: var(--ao); }
        .gd-user { display: flex; align-items: center; gap: 12px; }
        .gd-avatar {
          width: 34px; height: 34px; border-radius: 50%; background: var(--ao); color: #fff;
          display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px;
        }
        .gd-username { color: rgba(255,255,255,0.9); font-size: 14px; font-weight: 500; }
        .gd-logout {
          background: rgba(255,255,255,0.12); color: #fff; border: none; border-radius: 8px;
          padding: 8px 14px; font: 600 13px 'DM Sans', sans-serif; cursor: pointer; transition: background .2s;
        }
        .gd-logout:hover { background: rgba(255,255,255,0.24); }

        /* hero */
        .gd-hero { background: var(--pd); padding: 8px 32px 34px; color: #fff; }
        .gd-greeting { font-family: 'DM Serif Display', serif; font-size: clamp(24px,3.2vw,34px); margin: 0 0 4px; font-weight: 400; }
        .gd-greeting em { color: var(--ao); font-style: italic; }
        .gd-sub { color: rgba(255,255,255,0.6); font-size: 14.5px; margin: 0 0 26px; }
        .gd-tiles { display: grid; grid-template-columns: repeat(auto-fit,minmax(132px,1fr)); gap: 12px; max-width: 700px; }
        .gd-tile { background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.13); border-radius: 14px; padding: 14px 18px; }
        .gd-tile-num { font-family: 'DM Serif Display', serif; font-size: 30px; color: #fff; line-height: 1.05; }
        .gd-tile-lbl { font-size: 11px; color: rgba(255,255,255,0.55); margin-top: 5px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; }
        .gd-tile-sub { font-size: 12px; color: rgba(255,255,255,0.45); margin-top: 3px; }
        .gd-tile.tone-accent .gd-tile-num { color: var(--ao); }

        /* tabs */
        .gd-tabs {
          display: flex; background: var(--card); border-bottom: 1px solid var(--border);
          padding: 0 32px; position: sticky; top: 66px; z-index: 90; overflow-x: auto;
          scrollbar-width: none;
        }
        .gd-tabs::-webkit-scrollbar { display: none; }
        .gd-tab {
          padding: 15px 18px; font: 600 14px 'DM Sans', sans-serif; color: var(--muted);
          border: none; background: none; cursor: pointer; white-space: nowrap;
          border-bottom: 2.5px solid transparent; transition: color .18s, border-color .18s;
        }
        .gd-tab.is-on { color: var(--pd); border-bottom-color: var(--ao); }
        .gd-tab:hover:not(.is-on) { color: var(--pd); }

        .gd-body { max-width: 1080px; margin: 0 auto; padding: 28px 24px 64px; }
        .gd-h3 { font-family: 'DM Serif Display', serif; font-size: 19px; color: var(--pd); margin: 0 0 16px; font-weight: 400; }

        /* toolbar */
        .gd-toolbar { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; margin-bottom: 22px; }
        .gd-search {
          flex: 1; min-width: 200px; padding: 11px 15px; border-radius: 10px;
          border: 1.5px solid var(--border); background: var(--card);
          font: 400 14px 'DM Sans', sans-serif; color: var(--text); outline: none; transition: border-color .18s;
        }
        .gd-search:focus { border-color: var(--pd); }
        .gd-seg { display: flex; background: var(--card); border: 1.5px solid var(--border); border-radius: 10px; overflow: hidden; }
        .gd-seg button {
          padding: 10px 14px; border: none; background: none; cursor: pointer;
          font: 600 13px 'DM Sans', sans-serif; color: var(--muted); transition: background .16s, color .16s;
        }
        .gd-seg button.is-on { background: var(--pd); color: #fff; }
        .gd-seg button:hover:not(.is-on) { background: rgba(26,35,68,0.05); }

        /* session rows — ruled analysis paper */
        .gd-list { display: grid; gap: 14px; }
        .gd-row {
          position: relative; background: var(--card); border: 1px solid var(--border);
          border-radius: 16px; padding: 20px 22px 20px 30px; box-shadow: var(--shadow);
          display: grid; grid-template-columns: 168px 1fr auto; gap: 22px; align-items: center;
          transition: box-shadow .2s, transform .2s;
        }
        .gd-row::before {
          content: ''; position: absolute; left: 15px; top: 16px; bottom: 16px;
          width: 1.5px; background: rgba(232,98,42,0.28); border-radius: 2px;
        }
        .gd-row:hover { box-shadow: var(--shadow-lift); transform: translateY(-1px); }
        .gd-row.is-done { opacity: 0.72; }

        /* specimen */
        .gd-spec {
          position: relative; width: 168px; height: 92px; padding: 0; border-radius: 10px;
          overflow: hidden; border: 1px solid var(--border); background: var(--paper);
          cursor: zoom-in; display: block;
        }
        .gd-spec img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .25s; }
        .gd-spec:hover img { transform: scale(1.06); }
        .gd-spec-zoom {
          position: absolute; inset: auto 0 0 0; padding: 4px; background: rgba(26,35,68,0.78);
          color: #fff; font: 600 10px 'DM Sans', sans-serif; letter-spacing: 0.08em;
          text-transform: uppercase; opacity: 0; transition: opacity .2s;
        }
        .gd-spec:hover .gd-spec-zoom { opacity: 1; }
        .gd-spec-empty {
          cursor: default; display: flex; align-items: center; justify-content: center;
          text-align: center; padding: 10px; color: var(--muted); font-size: 11.5px;
          border-style: dashed; border-color: rgba(26,35,68,0.16);
          background-image: repeating-linear-gradient(transparent, transparent 17px, rgba(26,35,68,0.06) 17px, rgba(26,35,68,0.06) 18px);
        }

        .gd-name { font-family: 'DM Serif Display', serif; font-size: 18px; color: var(--pd); margin: 0 0 3px; font-weight: 400; }
        .gd-meta { font-size: 12.5px; color: var(--muted); margin: 0 0 10px; }
        .gd-when {
          display: inline-flex; align-items: center; gap: 7px;
          font: 500 12.5px 'DM Mono', monospace; color: var(--sage);
          background: rgba(74,124,111,0.09); padding: 4px 11px; border-radius: 6px;
        }
        .gd-badge {
          display: inline-flex; font: 700 10px 'DM Sans', sans-serif; text-transform: uppercase;
          letter-spacing: 0.07em; padding: 4px 10px; border-radius: 6px; margin-left: 8px;
        }
        .badge-scheduled { background: rgba(74,124,111,0.12); color: var(--sage); }
        .badge-completed { background: rgba(26,35,68,0.09); color: var(--pd); }
        .badge-cancelled { background: rgba(232,98,42,0.12); color: var(--ao); }
        .badge-unknown   { background: rgba(107,114,128,0.12); color: var(--muted); }
        .gd-note {
          margin: 11px 0 0; font-size: 13px; color: #4b5563; line-height: 1.6;
          background: var(--paper); border-left: 2px solid rgba(232,98,42,0.35);
          padding: 9px 13px; border-radius: 0 8px 8px 0;
        }

        .gd-acts { display: flex; flex-direction: column; gap: 8px; align-items: stretch; }
        .gd-btn {
          padding: 10px 17px; border-radius: 9px; border: none; cursor: pointer;
          font: 600 13px 'DM Sans', sans-serif; white-space: nowrap; transition: background .18s, transform .18s, border-color .18s;
        }
        .gd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .gd-btn-primary { background: var(--pd); color: #fff; }
        .gd-btn-primary:hover:not(:disabled) { background: var(--pd-lift); }
        .gd-btn-accent { background: var(--ao); color: #fff; }
        .gd-btn-accent:hover:not(:disabled) { background: #cc5220; }
        .gd-btn-ghost { background: var(--card); color: var(--pd); border: 1.5px solid var(--border); }
        .gd-btn-ghost:hover:not(:disabled) { border-color: var(--pd); }

        /* empty */
        .gd-empty { text-align: center; padding: 56px 24px; color: var(--muted); background: var(--card); border: 1px dashed var(--border); border-radius: 16px; }
        .gd-empty h4 { font-family: 'DM Serif Display', serif; color: var(--pd); font-size: 18px; margin: 0 0 6px; font-weight: 400; }
        .gd-empty p { margin: 0 0 14px; font-size: 13.5px; }

        /* calendar */
        .gd-cal { background: var(--card); border: 1px solid var(--border); border-radius: 18px; padding: 22px; box-shadow: var(--shadow); margin-bottom: 22px; }
        .gd-cal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
        .gd-cal-title { font-family: 'DM Serif Display', serif; font-size: 19px; color: var(--pd); }
        .gd-cal-nav { display: flex; gap: 8px; }
        .gd-cal-nav button {
          width: 34px; height: 34px; border-radius: 8px; border: 1.5px solid var(--border);
          background: var(--card); color: var(--pd); cursor: pointer; font-size: 15px; line-height: 1;
        }
        .gd-cal-nav button:hover { border-color: var(--pd); }
        .gd-grid { display: grid; grid-template-columns: repeat(7,1fr); gap: 5px; }
        .gd-dow { text-align: center; font: 700 10.5px 'DM Sans', sans-serif; color: var(--muted); letter-spacing: 0.08em; padding-bottom: 6px; }
        .gd-cell {
          aspect-ratio: 1 / 0.86; border-radius: 9px; border: 1px solid transparent; background: none;
          display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px;
          font: 500 13px 'DM Sans', sans-serif; color: var(--text); cursor: pointer; transition: background .15s, border-color .15s;
        }
        .gd-cell:hover:not(:disabled) { background: rgba(26,35,68,0.05); }
        .gd-cell:disabled { cursor: default; }
        .gd-cell.is-today { border-color: rgba(232,98,42,0.5); font-weight: 700; }
        .gd-cell.is-picked { background: var(--pd); color: #fff; }
        .gd-dots { display: flex; gap: 3px; height: 5px; }
        .gd-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--ao); }
        .gd-dot.slot { background: var(--sage); }
        .gd-cell.is-picked .gd-dot { background: #fff; }
        .gd-cal-legend { display: flex; gap: 16px; margin-top: 14px; font-size: 12px; color: var(--muted); }
        .gd-cal-legend span { display: inline-flex; align-items: center; gap: 6px; }

        /* forms */
        .gd-panel { background: var(--card); border: 1px solid var(--border); border-radius: 18px; padding: 26px; box-shadow: var(--shadow); margin-bottom: 24px; }
        .gd-fields { display: flex; gap: 14px; flex-wrap: wrap; }
        .gd-field { display: flex; flex-direction: column; gap: 6px; flex: 1; min-width: 145px; }
        .gd-label { font: 700 11px 'DM Sans', sans-serif; color: var(--pd); text-transform: uppercase; letter-spacing: 0.07em; }
        .gd-input {
          padding: 11px 13px; border-radius: 9px; border: 1.5px solid var(--border);
          font: 400 14px 'DM Sans', sans-serif; color: var(--text); outline: none;
          background: var(--card); transition: border-color .18s; width: 100%; box-sizing: border-box;
        }
        .gd-input:focus { border-color: var(--pd); }

        /* slot rows */
        .gd-slots { display: grid; gap: 10px; }
        .gd-slot {
          background: var(--card); border: 1px solid var(--border); border-radius: 12px;
          padding: 14px 18px; display: flex; align-items: center; justify-content: space-between; box-shadow: var(--shadow);
        }
        .gd-slot.is-expired { opacity: 0.55; }
        .gd-slot-day { font-size: 14px; font-weight: 600; }
        .gd-slot-time { font: 400 13px 'DM Mono', monospace; color: var(--muted); margin-top: 2px; }
        .gd-tag-expired { margin-left: 8px; color: var(--ao); font: 700 10px 'DM Sans', sans-serif; letter-spacing: 0.06em; }
        .gd-del {
          background: none; border: 1.5px solid rgba(232,98,42,0.3); color: var(--ao);
          border-radius: 8px; padding: 7px 14px; font: 600 13px 'DM Sans', sans-serif; cursor: pointer; transition: all .18s;
        }
        .gd-del:hover:not(:disabled) { background: var(--ao); color: #fff; border-color: var(--ao); }
        .gd-del:disabled { opacity: 0.4; cursor: not-allowed; }

        /* insights */
        .gd-money { display: grid; grid-template-columns: repeat(auto-fit,minmax(190px,1fr)); gap: 14px; margin-bottom: 24px; }
        .gd-money-card { background: var(--card); border: 1px solid var(--border); border-radius: 16px; padding: 20px 22px; box-shadow: var(--shadow); }
        .gd-money-num { font-family: 'DM Serif Display', serif; font-size: 28px; color: var(--pd); line-height: 1.1; }
        .gd-money-num.accent { color: var(--ao); }
        .gd-money-lbl { font: 700 10.5px 'DM Sans', sans-serif; color: var(--muted); text-transform: uppercase; letter-spacing: 0.07em; margin-top: 6px; }
        .gd-money-sub { font-size: 12px; color: var(--muted); margin-top: 5px; }
        .gd-bars { display: flex; align-items: flex-end; gap: 10px; height: 150px; margin-top: 8px; }
        .gd-bar-col { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; height: 100%; justify-content: flex-end; }
        .gd-bar { width: 100%; max-width: 46px; background: var(--pd); border-radius: 6px 6px 0 0; min-height: 3px; transition: height .3s; }
        .gd-bar.is-now { background: var(--ao); }
        .gd-bar-n { font: 500 12px 'DM Mono', monospace; color: var(--pd); }
        .gd-bar-m { font: 600 11px 'DM Sans', sans-serif; color: var(--muted); }
        .gd-split { display: grid; gap: 8px; margin-top: 4px; }
        .gd-split-row { display: grid; grid-template-columns: 84px 1fr 34px; align-items: center; gap: 12px; font-size: 12.5px; }
        .gd-split-track { height: 8px; background: rgba(26,35,68,0.07); border-radius: 5px; overflow: hidden; }
        .gd-split-fill { height: 100%; border-radius: 5px; }

        /* profile */
        .gd-profile { display: grid; grid-template-columns: 86px 1fr; gap: 24px; align-items: start; }
        .gd-pic { width: 86px; height: 86px; border-radius: 50%; object-fit: cover; border: 3px solid var(--border); }
        .gd-pic-ph {
          width: 86px; height: 86px; border-radius: 50%; background: var(--pd); color: #fff;
          display: flex; align-items: center; justify-content: center;
          font: 400 30px 'DM Serif Display', serif;
        }
        .gd-pname { font-family: 'DM Serif Display', serif; font-size: 23px; color: var(--pd); margin: 0 0 3px; font-weight: 400; }
        .gd-pspec { font-size: 14px; color: var(--muted); margin: 0 0 18px; }
        .gd-pgrid { display: grid; grid-template-columns: 1fr 1fr; gap: 11px; }
        .gd-pfield { background: var(--bg); border-radius: 10px; padding: 11px 14px; }
        .gd-pfield-l { font: 700 10.5px 'DM Sans', sans-serif; color: var(--muted); text-transform: uppercase; letter-spacing: 0.07em; }
        .gd-pfield-v { font-size: 14px; margin-top: 3px; font-weight: 500; }

        /* modal */
        .gd-modal-bg {
          position: fixed; inset: 0; background: rgba(26,35,68,0.55); backdrop-filter: blur(5px);
          display: flex; align-items: center; justify-content: center; z-index: 999; padding: 22px;
        }
        .gd-modal { background: #fff; border-radius: 20px; padding: 32px; width: 100%; max-width: 520px; box-shadow: 0 24px 60px rgba(26,35,68,0.24); }
        .gd-modal-t { font-family: 'DM Serif Display', serif; font-size: 21px; color: var(--pd); margin: 0 0 5px; font-weight: 400; }
        .gd-modal-s { font-size: 13.5px; color: var(--muted); margin: 0 0 18px; }
        .gd-textarea {
          width: 100%; min-height: 150px; padding: 14px 16px; border-radius: 11px;
          border: 1.5px solid var(--border); font: 400 14px 'DM Sans', sans-serif;
          line-height: 1.6; resize: vertical; outline: none; box-sizing: border-box; transition: border-color .18s;
        }
        .gd-textarea:focus { border-color: var(--pd); }
        .gd-check { display: flex; align-items: center; gap: 9px; margin-top: 14px; font-size: 13.5px; color: var(--text); cursor: pointer; }
        .gd-modal-acts { display: flex; gap: 10px; margin-top: 18px; justify-content: flex-end; }

        .gd-lightbox {
          position: fixed; inset: 0; background: rgba(0,0,0,0.88); display: flex;
          align-items: center; justify-content: center; z-index: 1000; cursor: zoom-out; padding: 24px;
        }
        .gd-lightbox img { max-width: 92vw; max-height: 86vh; border-radius: 10px; }

        @media (max-width: 720px) {
          .gd-header, .gd-tabs { padding-left: 16px; padding-right: 16px; }
          .gd-hero { padding: 8px 16px 26px; }
          .gd-body { padding: 20px 16px 48px; }
          .gd-row { grid-template-columns: 1fr; gap: 14px; padding: 18px 18px 18px 26px; }
          .gd-spec { width: 100%; height: 110px; }
          .gd-acts { flex-direction: row; }
          .gd-acts .gd-btn { flex: 1; }
          .gd-profile, .gd-pgrid { grid-template-columns: 1fr; }
          .gd-username { display: none; }
        }
      `}</style>

      <div className="gd-root">
        <header className="gd-header">
          <div className="gd-brand">UXAAH<span>.</span></div>
          <div className="gd-user">
            <div className="gd-avatar">{user?.name?.[0]?.toUpperCase() || 'G'}</div>
            <span className="gd-username">{user?.name}</span>
            <button className="gd-logout" onClick={() => { logout?.(); navigate('/graph-login'); }}>
              Sign out
            </button>
          </div>
        </header>

        <div className="gd-hero">
          <h2 className="gd-greeting">
            {greeting}, <em>{user?.name?.split(' ')[0] || 'there'}</em>
          </h2>
          <p className="gd-sub">
            {todayCount > 0
              ? `${todayCount} session${todayCount > 1 ? 's' : ''} on your slate today.`
              : 'Nothing booked for today.'}
          </p>
          <div className="gd-tiles">
            <Tile value={todayCount} label="Today" tone="accent" />
            <Tile value={upcomingCount} label="Upcoming" />
            <Tile value={completedCount} label="Completed" />
            <Tile value={openSlotCount} label="Open slots" />
          </div>
        </div>

        <nav className="gd-tabs">
          {[
            ['sessions', 'Sessions'],
            ['calendar', 'Calendar'],
            ['availability', 'Availability'],
            ['insights', 'Insights'],
            ['profile', 'Profile'],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`gd-tab${activeTab === key ? ' is-on' : ''}`}
              onClick={() => setActiveTab(key)}
            >
              {label}
            </button>
          ))}
        </nav>

        <div className="gd-body">
          {loading && (
            <div className="gd-empty">
              <p style={{ margin: 0 }}>Loading your dashboard…</p>
            </div>
          )}

          {/* ── SESSIONS ── */}
          {!loading && activeTab === 'sessions' && (
            <>
              <div className="gd-toolbar">
                <input
                  className="gd-search"
                  placeholder="Search by client name, email, or phone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <div className="gd-seg">
                  {[['all', 'All'], ['scheduled', 'Scheduled'], ['completed', 'Done'], ['cancelled', 'Cancelled']].map(
                    ([v, l]) => (
                      <button
                        key={v}
                        className={statusFilter === v ? 'is-on' : ''}
                        onClick={() => setStatusFilter(v)}
                      >
                        {l}
                      </button>
                    )
                  )}
                </div>
                <div className="gd-seg">
                  <button className={sortDir === 'asc' ? 'is-on' : ''} onClick={() => setSortDir('asc')}>
                    Soonest
                  </button>
                  <button className={sortDir === 'desc' ? 'is-on' : ''} onClick={() => setSortDir('desc')}>
                    Latest
                  </button>
                </div>
              </div>

              {pickedDay && (
                <p style={{ margin: '-8px 0 18px', fontSize: 13, color: '#6b7280' }}>
                  Showing {fmtDay(pickedDay)} only.{' '}
                  <button
                    onClick={() => setPickedDay(null)}
                    style={{ background: 'none', border: 'none', color: '#e8622a', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                  >
                    Clear
                  </button>
                </p>
              )}

              {visibleSessions.length === 0 ? (
                <div className="gd-empty">
                  <h4>{sessions.length === 0 ? 'No sessions booked yet' : 'Nothing matches those filters'}</h4>
                  <p>
                    {sessions.length === 0
                      ? 'Open up some availability and clients can start booking you.'
                      : 'Try a different search term or clear the status filter.'}
                  </p>
                  {sessions.length === 0 ? (
                    <button className="gd-btn gd-btn-primary" onClick={() => setActiveTab('availability')}>
                      Add availability
                    </button>
                  ) : (
                    <button
                      className="gd-btn gd-btn-ghost"
                      onClick={() => { setQuery(''); setStatusFilter('all'); setPickedDay(null); }}
                    >
                      Clear filters
                    </button>
                  )}
                </div>
              ) : (
                <div className="gd-list">
                  {visibleSessions.map((s) => {
                    const src = s.handwriting_sample_path ? `${BACKEND_URL}${s.handwriting_sample_path}` : null;
                    return (
                      <article className={`gd-row${s._status === 'completed' ? ' is-done' : ''}`} key={s.id}>
                        <Specimen src={src} onOpen={setLightbox} />

                        <div>
                          <h4 className="gd-name">{s.client_name || 'Unnamed client'}</h4>
                          <p className="gd-meta">
                            {s.client_email}
                            {s.client_phone && s.client_phone !== 'N/A' && ` · ${s.client_phone}`}
                          </p>
                          <span className="gd-when">
                            {fmtDay(s._d)} · {s._d ? fmtTime(s._d) : s.time || '—'}
                          </span>
                          <span className={`gd-badge badge-${statusClass(s._status)}`}>{s._status}</span>
                          {s.report_notes && (
                            <p className="gd-note">
                              {s.report_notes.length > 160 ? `${s.report_notes.slice(0, 160)}…` : s.report_notes}
                            </p>
                          )}
                        </div>

                        <div className="gd-acts">
                          <button
                            className="gd-btn gd-btn-accent"
                            onClick={() => handleStartCall(s)}
                            disabled={s._status === 'cancelled'}
                          >
                            Start session
                          </button>
                          <button
                            className="gd-btn gd-btn-ghost"
                            onClick={() => {
                              setNotesTarget(s);
                              setNotesText(s.report_notes || '');
                              setMarkDone(s._status === 'completed');
                            }}
                          >
                            {s.report_notes ? 'Edit notes' : 'Add notes'}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ── CALENDAR ── */}
          {!loading && activeTab === 'calendar' && (
            <>
              <div className="gd-cal">
                <div className="gd-cal-head">
                  <div className="gd-cal-title">
                    {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
                  </div>
                  <div className="gd-cal-nav">
                    <button
                      onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                      aria-label="Previous month"
                    >
                      ‹
                    </button>
                    <button onClick={() => setCursor(new Date())} style={{ width: 'auto', padding: '0 12px', fontSize: 13, fontWeight: 600 }}>
                      Today
                    </button>
                    <button
                      onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                      aria-label="Next month"
                    >
                      ›
                    </button>
                  </div>
                </div>

                <div className="gd-grid">
                  {DOW.map((d, i) => (
                    <div className="gd-dow" key={i}>{d}</div>
                  ))}
                  {buildMonthCells(cursor).map((d, i) => {
                    if (!d) return <div key={`p${i}`} />;
                    const k = dayKey(d);
                    const booked = sessionsByDay[k]?.length || 0;
                    const open = slotsByDay[k] || 0;
                    return (
                      <button
                        key={k}
                        className={`gd-cell${isSameDay(d, now) ? ' is-today' : ''}${isSameDay(d, pickedDay) ? ' is-picked' : ''}`}
                        onClick={() => setPickedDay(isSameDay(d, pickedDay) ? null : d)}
                      >
                        <span>{d.getDate()}</span>
                        <span className="gd-dots">
                          {booked > 0 && <i className="gd-dot" />}
                          {open > 0 && <i className="gd-dot slot" />}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="gd-cal-legend">
                  <span><i className="gd-dot" /> Booked session</span>
                  <span><i className="gd-dot slot" /> Open slot</span>
                </div>
              </div>

              <h3 className="gd-h3">
                {pickedDay ? fmtDay(pickedDay) : 'Pick a day to see its sessions'}
              </h3>
              {pickedDay && (
                (sessionsByDay[dayKey(pickedDay)] || []).length === 0 ? (
                  <div className="gd-empty"><p style={{ margin: 0 }}>Nothing booked on this day.</p></div>
                ) : (
                  <div className="gd-slots">
                    {(sessionsByDay[dayKey(pickedDay)] || [])
                      .sort((a, b) => a._d - b._d)
                      .map((s) => (
                        <div className="gd-slot" key={s.id}>
                          <div>
                            <div className="gd-slot-day">{s.client_name}</div>
                            <div className="gd-slot-time">{fmtTime(s._d)} · {s._status}</div>
                          </div>
                          <button className="gd-btn gd-btn-ghost" onClick={() => { setPickedDay(pickedDay); setActiveTab('sessions'); }}>
                            Open
                          </button>
                        </div>
                      ))}
                  </div>
                )
              )}
            </>
          )}

          {/* ── AVAILABILITY ── */}
          {!loading && activeTab === 'availability' && (
            <>
              <form className="gd-panel" onSubmit={handleAddSlot}>
                <h3 className="gd-h3">Open a new slot</h3>
                <div className="gd-fields">
                  <div className="gd-field">
                    <label className="gd-label" htmlFor="gd-date">Date</label>
                    <input
                      id="gd-date" type="date" className="gd-input" value={slotDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setSlotDate(e.target.value)} required
                    />
                  </div>
                  <div className="gd-field">
                    <label className="gd-label" htmlFor="gd-start">Starts</label>
                    <input id="gd-start" type="time" className="gd-input" value={slotStart}
                      onChange={(e) => setSlotStart(e.target.value)} required />
                  </div>
                  <div className="gd-field">
                    <label className="gd-label" htmlFor="gd-end">Ends</label>
                    <input id="gd-end" type="time" className="gd-input" value={slotEnd}
                      onChange={(e) => setSlotEnd(e.target.value)} required />
                  </div>
                  <div className="gd-field" style={{ justifyContent: 'flex-end', flex: '0 0 auto' }}>
                    <label className="gd-label" style={{ visibility: 'hidden' }}>submit</label>
                    <button type="submit" className="gd-btn gd-btn-primary" disabled={addingSlot} style={{ padding: '12px 22px' }}>
                      {addingSlot ? 'Adding…' : 'Add slot'}
                    </button>
                  </div>
                </div>
              </form>

              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <h3 className="gd-h3">Your slots ({openSlotCount} open)</h3>
                <label className="gd-check" style={{ marginTop: 0, marginBottom: 16, fontSize: 13 }}>
                  <input type="checkbox" checked={hideExpired} onChange={(e) => setHideExpired(e.target.checked)} />
                  Hide expired
                </label>
              </div>

              {(() => {
                const shown = slots
                  .filter((s) => !hideExpired || (s.status !== 'expired' && s.display_status !== 'expired'))
                  .sort((a, b) => (slotDateOf(a) || 0) - (slotDateOf(b) || 0));
                if (shown.length === 0)
                  return (
                    <div className="gd-empty">
                      <h4>No open slots</h4>
                      <p>Add a time range above and clients can book it straight away.</p>
                    </div>
                  );
                return (
                  <div className="gd-slots">
                    {shown.map((slot) => {
                      const expired = slot.status === 'expired' || slot.display_status === 'expired';
                      const d = slotDateOf(slot);
                      const e = slot.end_time ? new Date(String(slot.end_time).replace(' ', 'T')) : null;
                      return (
                        <div className={`gd-slot${expired ? ' is-expired' : ''}`} key={slot.id}>
                          <div>
                            <div className="gd-slot-day">
                              {fmtDay(d)}
                              {expired && <span className="gd-tag-expired">EXPIRED</span>}
                            </div>
                            <div className="gd-slot-time">
                              {fmtTime(d)} → {fmtTime(e)}
                            </div>
                          </div>
                          <button className="gd-del" onClick={() => handleDeleteSlot(slot.id)} disabled={expired}>
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}

          {/* ── INSIGHTS ── */}
          {!loading && activeTab === 'insights' && (
            <>
              <div className="gd-money">
                <div className="gd-money-card">
                  <div className="gd-money-num accent">{rupees(earned)}</div>
                  <div className="gd-money-lbl">Earned</div>
                  <div className="gd-money-sub">{completedCount} completed × {rupees(fee)}</div>
                </div>
                <div className="gd-money-card">
                  <div className="gd-money-num">{rupees(pipeline)}</div>
                  <div className="gd-money-lbl">In the pipeline</div>
                  <div className="gd-money-sub">{upcomingCount} still to run</div>
                </div>
                <div className="gd-money-card">
                  <div className="gd-money-num">{rupees(fee)}</div>
                  <div className="gd-money-lbl">Your fee</div>
                  <div className="gd-money-sub">
                    {fee === 0 ? 'Set a fee in Profile' : 'Per analysis'}
                  </div>
                </div>
              </div>

              <div className="gd-panel">
                <h3 className="gd-h3">Sessions, last six months</h3>
                <div className="gd-bars">
                  {monthlyVolume.map((m, i) => (
                    <div className="gd-bar-col" key={`${m.label}-${i}`}>
                      <span className="gd-bar-n">{m.count}</span>
                      <div
                        className={`gd-bar${i === monthlyVolume.length - 1 ? ' is-now' : ''}`}
                        style={{ height: `${(m.count / peakVolume) * 100}%` }}
                      />
                      <span className="gd-bar-m">{m.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="gd-panel">
                <h3 className="gd-h3">Where your bookings stand</h3>
                <div className="gd-split">
                  {[
                    ['Scheduled', decorated.filter((s) => s._status === 'scheduled').length, '#4a7c6f'],
                    ['Completed', completedCount, '#1a2344'],
                    ['Cancelled', decorated.filter((s) => s._status === 'cancelled').length, '#e8622a'],
                  ].map(([label, count, color]) => (
                    <div className="gd-split-row" key={label}>
                      <span style={{ color: '#6b7280', fontWeight: 600 }}>{label}</span>
                      <div className="gd-split-track">
                        <div
                          className="gd-split-fill"
                          style={{
                            width: `${decorated.length ? (count / decorated.length) * 100 : 0}%`,
                            background: color,
                          }}
                        />
                      </div>
                      <span style={{ fontFamily: "'DM Mono', monospace", textAlign: 'right' }}>{count}</span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 12, color: '#6b7280', marginTop: 16, marginBottom: 0 }}>
                  Earnings are calculated from your listed fee, not from payment records.
                </p>
              </div>
            </>
          )}

          {/* ── PROFILE ── */}
          {!loading && activeTab === 'profile' && !profile && (
            <div className="gd-empty">
              <h4>Profile didn't load</h4>
              <p>Refresh the page, or check that you're still signed in.</p>
            </div>
          )}

          {!loading && activeTab === 'profile' && profile && !editing && (
            <div className="gd-panel">
              <div className="gd-profile">
                {profile.image ? (
                  <img src={`${BACKEND_URL}/uploads/${profile.image}`} alt={profile.name} className="gd-pic" />
                ) : (
                  <div className="gd-pic-ph">{profile.name?.[0]?.toUpperCase() || 'G'}</div>
                )}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <h3 className="gd-pname">{profile.name}</h3>
                      <p className="gd-pspec">{profile.specialisation || 'Graphologist'}</p>
                    </div>
                    <button className="gd-btn gd-btn-ghost" onClick={startEditing}>Edit profile</button>
                  </div>
                  <div className="gd-pgrid">
                    {[
                      ['Email', profile.email],
                      ['Phone', profile.PhoneNumber || '—'],
                      ['Experience', profile.experience || '—'],
                      ['Fee per analysis', profile.price ? rupees(profile.price) : '—'],
                    ].map(([l, v]) => (
                      <div className="gd-pfield" key={l}>
                        <div className="gd-pfield-l">{l}</div>
                        <div className="gd-pfield-v">{v}</div>
                      </div>
                    ))}
                  </div>
                  {profile.bio && (
                    <div style={{ marginTop: 15, padding: '13px 16px', background: '#f7f8fb', borderRadius: 11 }}>
                      <div className="gd-pfield-l">Bio</div>
                      <p style={{ marginTop: 6, marginBottom: 0, fontSize: 14, lineHeight: 1.7 }}>{profile.bio}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!loading && activeTab === 'profile' && profile && editing && (
            <form className="gd-panel" onSubmit={handleSaveProfile}>
              <h3 className="gd-h3">Edit your profile</h3>
              <div className="gd-fields" style={{ marginBottom: 14 }}>
                <div className="gd-field">
                  <label className="gd-label" htmlFor="p-name">Name</label>
                  <input id="p-name" className="gd-input" value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })} required />
                </div>
                <div className="gd-field">
                  <label className="gd-label" htmlFor="p-phone">Phone</label>
                  <input id="p-phone" className="gd-input" value={draft.PhoneNumber}
                    onChange={(e) => setDraft({ ...draft, PhoneNumber: e.target.value })} />
                </div>
              </div>
              <div className="gd-fields" style={{ marginBottom: 14 }}>
                <div className="gd-field">
                  <label className="gd-label" htmlFor="p-spec">Specialisation</label>
                  <input id="p-spec" className="gd-input" value={draft.specialisation}
                    placeholder="e.g. Forensic document examination"
                    onChange={(e) => setDraft({ ...draft, specialisation: e.target.value })} />
                </div>
                <div className="gd-field">
                  <label className="gd-label" htmlFor="p-exp">Experience</label>
                  <input id="p-exp" className="gd-input" value={draft.experience}
                    placeholder="e.g. 8 years" onChange={(e) => setDraft({ ...draft, experience: e.target.value })} />
                </div>
                <div className="gd-field">
                  <label className="gd-label" htmlFor="p-price">Fee (₹)</label>
                  <input id="p-price" type="number" min="0" className="gd-input" value={draft.price}
                    onChange={(e) => setDraft({ ...draft, price: e.target.value })} />
                </div>
              </div>
              <div className="gd-field" style={{ marginBottom: 18 }}>
                <label className="gd-label" htmlFor="p-bio">Bio</label>
                <textarea id="p-bio" className="gd-textarea" style={{ minHeight: 110 }} value={draft.bio}
                  placeholder="How you work, what clients can expect from a session."
                  onChange={(e) => setDraft({ ...draft, bio: e.target.value })} />
              </div>
              <div className="gd-modal-acts" style={{ marginTop: 0 }}>
                <button type="button" className="gd-btn gd-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                <button type="submit" className="gd-btn gd-btn-primary" disabled={savingProfile}>
                  {savingProfile ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* notes modal */}
      {notesTarget && (
        <div className="gd-modal-bg" onClick={() => setNotesTarget(null)}>
          <div className="gd-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="gd-modal-t">Session notes</h3>
            <p className="gd-modal-s">
              {notesTarget.client_name} · {fmtDay(sessionDate(notesTarget))}
            </p>
            <textarea
              className="gd-textarea"
              placeholder="Findings, stroke observations, follow-ups…"
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              autoFocus
            />
            <label className="gd-check">
              <input type="checkbox" checked={markDone} onChange={(e) => setMarkDone(e.target.checked)} />
              Mark this session completed
            </label>
            <div className="gd-modal-acts">
              <button className="gd-btn gd-btn-ghost" onClick={() => setNotesTarget(null)}>Cancel</button>
              <button className="gd-btn gd-btn-primary" onClick={handleSaveNotes} disabled={savingNotes}>
                {savingNotes ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* lightbox */}
      {lightbox && (
        <div className="gd-lightbox" onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="Handwriting specimen — click to close" />
        </div>
      )}
    </>
  );
};

export default GraphDashboard;